import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ColorScheme = 'blue' | 'green' | 'purple' | 'orange' | 'red';

export interface ThemeConfig {
  theme: Theme;
  colorScheme: ColorScheme;
  fontSize: 'sm' | 'md' | 'lg';
  compactMode: boolean;
  animations: boolean;
}

interface ThemeContextType {
  config: ThemeConfig;
  setTheme: (theme: Theme) => void;
  setColorScheme: (colorScheme: ColorScheme) => void;
  setFontSize: (fontSize: 'sm' | 'md' | 'lg') => void;
  setCompactMode: (compact: boolean) => void;
  setAnimations: (animations: boolean) => void;
  resetTheme: () => void;
}

const defaultConfig: ThemeConfig = {
  theme: 'system',
  colorScheme: 'blue',
  fontSize: 'md',
  compactMode: false,
  animations: true
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<ThemeConfig>(defaultConfig);

  // Carregar configuração salva
  useEffect(() => {
    const savedConfig = localStorage.getItem('tamoaki_theme_config');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        setConfig({ ...defaultConfig, ...parsed });
      } catch (error) {
        console.error('Erro ao carregar configuração de tema:', error);
      }
    }
  }, []);

  // Salvar configuração quando mudar
  useEffect(() => {
    localStorage.setItem('tamoaki_theme_config', JSON.stringify(config));
  }, [config]);

  // Aplicar tema ao documento
  useEffect(() => {
    const root = document.documentElement;

    // Aplicar tema (data-theme + classe .dark para variantes Tailwind)
    if (config.theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.setAttribute('data-theme', systemTheme);
    } else {
      root.setAttribute('data-theme', config.theme);
    }

    root.classList.toggle('dark', root.getAttribute('data-theme') === 'dark');

    // Aplicar esquema de cores
    root.setAttribute('data-color-scheme', config.colorScheme);

    // Aplicar tamanho da fonte
    root.setAttribute('data-font-size', config.fontSize);

    // Aplicar modo compacto
    if (config.compactMode) {
      root.classList.add('compact-mode');
    } else {
      root.classList.remove('compact-mode');
    }

    // Aplicar animações
    if (!config.animations) {
      root.classList.add('no-animations');
    } else {
      root.classList.remove('no-animations');
    }
  }, [config]);

  // Escutar mudanças no tema do sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (config.theme === 'system') {
        const root = document.documentElement;
        const systemTheme = mediaQuery.matches ? 'dark' : 'light';
        root.setAttribute('data-theme', systemTheme);
        root.classList.toggle('dark', systemTheme === 'dark');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [config.theme]);

  const setTheme = (theme: Theme) => {
    setConfig(prev => ({ ...prev, theme }));
  };

  const setColorScheme = (colorScheme: ColorScheme) => {
    setConfig(prev => ({ ...prev, colorScheme }));
  };

  const setFontSize = (fontSize: 'sm' | 'md' | 'lg') => {
    setConfig(prev => ({ ...prev, fontSize }));
  };

  const setCompactMode = (compactMode: boolean) => {
    setConfig(prev => ({ ...prev, compactMode }));
  };

  const setAnimations = (animations: boolean) => {
    setConfig(prev => ({ ...prev, animations }));
  };

  const resetTheme = () => {
    setConfig(defaultConfig);
  };

  const contextValue: ThemeContextType = {
    config,
    setTheme,
    setColorScheme,
    setFontSize,
    setCompactMode,
    setAnimations,
    resetTheme
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
